import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { errorMessage, getProject } from '../../api';
import { paths } from '../../app/paths';
import { useAsyncData } from '../../hooks/useAsyncData';
import { buildFeasibilityInput } from '../feasibility/feasibilityInput';
import { useFeasibility } from '../feasibility/useFeasibility';
import { Button, Card, EmptyState, ErrorState, Modal, Skeleton, Stepper } from '../../ui';
import type { Project, TargetGroup } from '../../types';
import { PlanningStrip } from './PlanningStrip';
import { StepBasics } from './StepBasics';
import { StepExclusions } from './StepExclusions';
import { StepProfiling } from './StepProfiling';
import { StepQuotas } from './StepQuotas';
import { StepRegions } from './StepRegions';
import { WizardContext } from './WizardContext';
import type { BasicsValues } from './WizardContext';

const STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'regions', label: 'Regions' },
  { key: 'profiling', label: 'Profiling' },
  { key: 'quotas', label: 'Quotas' },
  { key: 'exclusions', label: 'Exclusions' },
];
const STEP_COMPONENTS = { basics: StepBasics, regions: StepRegions, profiling: StepProfiling, quotas: StepQuotas, exclusions: StepExclusions };

export function TargetGroupWizard() {
  const { projectId, tgId, step } = useParams();
  const navigate = useNavigate();
  const { data: project, error, loading, reload } = useAsyncData<Project>((signal) => getProject(projectId, { signal }), [projectId]);
  const [liveBasics, setLiveBasics] = useState<BasicsValues | null>(null);
  const dirtyRef = useRef(false); // set by the current step; read when the user tries to leave it
  const [leaveTo, setLeaveTo] = useState<string | null>(null); // step the user wants to go to while changes are unsaved

  const targetGroup = project ? (project.targetGroups || []).find((t) => t.id === tgId) : undefined;

  // The planning strip's feasible figure comes from the server. On Basics it follows what is typed; on the other
  // steps it uses the saved basics. Region and profiling are the saved ones.
  const feasibilityValues = step === 'basics' && liveBasics ? liveBasics : (targetGroup && targetGroup.basicSetting) || liveBasics;
  const feasibilityInput = useMemo(
    () => buildFeasibilityInput({ basics: feasibilityValues, region: targetGroup && targetGroup.region, profilingAnswers: targetGroup && targetGroup.profilingAnswers }),
    [feasibilityValues, targetGroup]
  );
  const feasibility = useFeasibility(feasibilityInput);

  const navigateTo = useCallback((key: string) => {
    // Leaving the step settles the question. The step's own cleanup only runs a moment after the next step
    // renders, so without this a quick click right after a save would still see "unsaved changes".
    dirtyRef.current = false;
    if (key === 'done') navigate(paths.project(projectId));
    else navigate(paths.targetGroup(projectId, tgId, key));
  }, [navigate, projectId, tgId]);

  const goStep = useCallback((key: string, opts?: { force?: boolean }) => {
    if (dirtyRef.current && !(opts && opts.force)) { setLeaveTo(key); return; }
    navigateTo(key);
  }, [navigateTo]);

  const setDirty = useCallback((value: boolean) => { dirtyRef.current = Boolean(value); }, []);

  // Closing or reloading the tab with unsaved changes gets the browser's own warning.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const ctx = useMemo(
    // Only read below the guards that make sure both exist.
    () => ({ project: project as Project, targetGroup: targetGroup as TargetGroup, reload, goStep, setLiveBasics, setDirty }),
    [project, targetGroup, reload, goStep, setDirty]
  );

  if (loading && !project) {
    return <div className="mx-auto max-w-6xl space-y-4" aria-busy="true"><Skeleton className="h-8 w-64" /><Skeleton className="h-10 w-full" /><Skeleton className="h-80 w-full" /></div>;
  }
  if (error) return <Card className="mx-auto max-w-6xl"><ErrorState title="Could not load this target group" message={errorMessage(error)} onRetry={reload} /></Card>;
  if (!project || !targetGroup) {
    return (
      <Card className="mx-auto max-w-6xl">
        <EmptyState title="Target group not found" description="It may have been removed, or the link is wrong." action={<Link to={project ? paths.project(project.id) : paths.projects()} className="text-sm font-semibold text-brand-700 hover:underline">Back to {project ? 'the campaign' : 'campaigns'}</Link>} />
      </Card>
    );
  }

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  if (stepIndex === -1) return <Navigate to={paths.targetGroup(projectId, tgId, 'basics')} replace />;
  // Later steps need the country, age range and completes saved in Basics.
  const basicsSaved = Boolean(targetGroup.basicSetting);
  if (!basicsSaved && step !== 'basics') return <Navigate to={paths.targetGroup(projectId, tgId, 'basics')} replace />;

  const StepComponent = STEP_COMPONENTS[step as keyof typeof STEP_COMPONENTS];
  // Basics step feeds live values; other steps use the saved settings.

  return (
    <WizardContext.Provider value={ctx}>
      <div className="mx-auto max-w-7xl">
        <nav aria-label="Breadcrumb" className="text-xs text-muted">
          <Link to={paths.projects()} className="hover:underline">Campaigns</Link> <span aria-hidden="true">›</span>{' '}
          <Link to={paths.project(project.id)} className="hover:underline">{project.name}</Link> <span aria-hidden="true">›</span>{' '}
          <span className="text-ink">{targetGroup.name || 'Target group'}</span>
        </nav>
        <div className="mb-4 mt-1 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <h1 className="text-2xl font-bold tracking-tight text-ink">{targetGroup.name || 'Target group'}</h1>
          <Stepper compact steps={STEPS} current={step as string} maxReachedIndex={basicsSaved ? STEPS.length - 1 : 0} onSelect={goStep} />
        </div>

        <PlanningStrip values={feasibilityValues} feasibility={feasibility} />

        <div key={step}><StepComponent /></div>

        <Modal
          open={leaveTo !== null}
          size="sm"
          title="Discard unsaved changes?"
          subtitle="What you changed on this step has not been saved."
          onClose={() => setLeaveTo(null)}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setLeaveTo(null)}>Stay and keep editing</Button>
              <Button onClick={() => { const to = leaveTo; dirtyRef.current = false; setLeaveTo(null); if (to) navigateTo(to); }}>Discard and leave</Button>
            </>
          )}
        >
          <p className="text-sm text-muted">Use Save on the step to keep your changes.</p>
        </Modal>
      </div>
    </WizardContext.Provider>
  );
}
